import React, { createContext, useContext, useEffect, useRef, useState, useReducer, useCallback } from 'react';
import { useParams } from 'react-router-dom'
import { useFlags } from 'launchdarkly-react-client-sdk';
import { UserContext } from 'components/Providers/Providers';
import { ActiveTabContext } from 'components/Providers/ActiveTabProvider';

import { createConnection, createHub } from 'api/signalr/hubConnection';
import { useApolloClient } from '@apollo/client';
import { GET_CURRENT_TENDER, GET_LOAD_BY_ID, GET_LOAD_DETAIL_DRAWER, GET_LOAD_DETAIL_DRAWER_V2 } from 'api/queries';
import { ConnectionMethodType } from 'sharedTypes/carrier/carrier';

export const BROADCAST_LOAD_BOARD = 'BroadcastLoadUpdate';
export const NOTIFY_LOAD_CHANGE_EVENT = 'NotifyLoadChange';
const BROADCAST_USER_COUNT_EVENT = "BroadcastLoadUserConnectedToGroupCount";

export const JOIN_USER_CONNECTED_GROUP = "JoinUserConnectedGroupAsync";
export const CHANGE_USER_CONNECTED_GROUP = 'ChangeUserConnectedGroupAsync';
export const LEAVE_USER_CONNECTED_GROUP = 'LeaveUserConnectedGroupAsync';
export const JOIN_LOAD_NUMBER_GROUP = 'LoadJoinLoadNumberGroup';
export const CHANGE_LOAD_NUMBER_GROUP = 'LoadChangeLoadNumberGroup';

const EXTERNAL_LOAD_BOARD_HUB = 'ExternalLoadBoardHub';
const LOAD_SERVICE_HUB = 'LoadServiceHub';
const LOAD_OVERVIEW_SERVICE_HUB = 'LoadOverviewServiceHub';
export const CONNECTED = 1;

export const UpdatedLoadsContext = createContext(null);
export const SignalrRefetchContext = createContext(null);
export const LoadServiceHubsContext = createContext(null);
export const UserCountContext = createContext(null);
export const CurrentLoadsContext = createContext(null);
export const LoadDrawerIdContext = createContext(null);

const initialState = {
  currentLoadIds: [] as Array<number>,
  updatedLoads: [] as any,
  loadDrawerLoadId: null as number,
  signalrRefetch: false
};

type ACTIONS = 'CLEAR_REFETCH' | 'LOAD_BOARD_REMOVE_LOAD' | 'LOAD_DETAIL_REFETCH' | 'SET_EXISTING_LOADS' | 'SET_LOAD_DRAWER_LOAD_ID' | 'CHECK_FOR_DRAWER_UPDATE';

const signalrReducer = (state: Record<any, any>, action: { type: ACTIONS, value?: any }) => {
  /* eslint-disable indent */
  switch (action.type) {
    case 'CLEAR_REFETCH':
      return {
        ...state,
        signalrRefetch: false
      };
    case 'SET_EXISTING_LOADS':
      return {
        ...state,
        currentLoadIds: action.value
      };
    case 'LOAD_BOARD_REMOVE_LOAD': {
      const existingLoads = state.currentLoadIds;
      const anyUpdatedInExisting = existingLoads.includes(action.value.LoadNumber);

      if (anyUpdatedInExisting) {
        return {
          ...state,
          updatedLoads: [action.value]
        }
      }
      return state;
    }
    case 'LOAD_DETAIL_REFETCH':
      return {
        ...state,
        signalrRefetch: true,
        updatedLoads: [action.value]
      }
    case 'SET_LOAD_DRAWER_LOAD_ID':
      return {
        ...state,
        loadDrawerLoadId: action.value
      }
    default:
      return state
  }
  /* eslint-enable indent */
};

export const LoadSignalrProvider = ({ children }: { children: React.ReactNode }) => {
  const client = useApolloClient()
  const { LOAD_SERVICE_THROUGHPUT_FIX } = useFlags();
  const [serviceConnection, setServiceConnection] = useState({ loadServiceHub: null, loadBoardHub: null, LoadOverviewServiceHub: null });
  const [userCount, setUserCount] = useState(0);
  const { loadNumber } = useParams<{ loadNumber: string }>();
  const currentUser = useContext(UserContext);
  const isActiveTab = useContext(ActiveTabContext);
  const [
    {
      updatedLoads,
      signalrRefetch,
      loadDrawerLoadId
    }, dispatch
  ] = useReducer(signalrReducer, initialState)

  const setCurrentLoads = useCallback((vals: any) => dispatch({ value: vals, type: 'SET_EXISTING_LOADS' }), [dispatch]);
  const setSignalrRefetch = useCallback(() => dispatch({ type: 'CLEAR_REFETCH' }), [dispatch]);
  const setLoadDrawerId = useCallback((id) => dispatch({ type: 'SET_LOAD_DRAWER_LOAD_ID', value: id }), [dispatch]);

  const externalLoadBoardHub = useRef(null);

  useEffect(() => {
    if (isActiveTab) {
      const loadSVCConnection = createConnection(process.env.LOAD_SERVICE_SIGNALR_ROOT.replace('api/v1/', 'signalr'));
      externalLoadBoardHub.current = createHub(loadSVCConnection, EXTERNAL_LOAD_BOARD_HUB);
      const loadServiceHub = createHub(loadSVCConnection, LOAD_SERVICE_HUB);
      const LoadOverviewServiceHub = createHub(loadSVCConnection, LOAD_OVERVIEW_SERVICE_HUB);
      if (loadNumber) {
        LoadOverviewServiceHub.on(NOTIFY_LOAD_CHANGE_EVENT, (rawLoadData: any) => {
          dispatch({ type: 'LOAD_DETAIL_REFETCH', value: rawLoadData });
        })
        if(currentUser.ConnectionMethodTypeId === ConnectionMethodType.EDI){
          externalLoadBoardHub.current.on(BROADCAST_LOAD_BOARD, async (rawLoadData: any) => {
            const cachedTender = client.readQuery({
              query: GET_CURRENT_TENDER,
              variables: {
                loadNumber: rawLoadData.LoadNumber
              }
            })
            if(cachedTender){
              if(rawLoadData.OrderStatus !== "Assigned"){
                await client.refetchQueries({
                  include: [GET_LOAD_BY_ID]
                });
              }
              client.refetchQueries({
                include: [GET_CURRENT_TENDER]
              });
            }
          });
        }
      } else {
        externalLoadBoardHub.current.on(BROADCAST_LOAD_BOARD, (rawLoadData: any) => {
          if (rawLoadData.OrderStatus !== "Available") {
            dispatch({ type: 'LOAD_BOARD_REMOVE_LOAD', value: rawLoadData })
          }
        });
      }

      loadServiceHub.on(BROADCAST_USER_COUNT_EVENT, (nextUserCount: Array<any>) => {
        setUserCount(nextUserCount?.length || 0);
      });

      loadSVCConnection.start().done(() => {
        setServiceConnection({ loadServiceHub, loadBoardHub: externalLoadBoardHub.current, LoadOverviewServiceHub });
      })

      return () => {
        loadSVCConnection.stop(true, true)
        setServiceConnection({ loadServiceHub: null, loadBoardHub: null, LoadOverviewServiceHub: null })
      };
    }
  }, [isActiveTab, currentUser.UserId]) //eslint-disable-line


  useEffect(() => {
    //This useEffect allows us to determine if we should refetch load details if the drawer is open
    if(!externalLoadBoardHub.current) {
      return;
    }
    if(loadDrawerLoadId) {
      //If drawer is open add new method refetching load upon event
      externalLoadBoardHub.current.on(BROADCAST_LOAD_BOARD, (rawLoadData: any) => {
        if(rawLoadData.LoadNumber === loadDrawerLoadId && rawLoadData.OrderStatus === 'Available') {
          const query = LOAD_SERVICE_THROUGHPUT_FIX ? GET_LOAD_DETAIL_DRAWER_V2 : GET_LOAD_DETAIL_DRAWER;
          client.refetchQueries({
            include: [query]
          })
        }
      })
    } else {
      //On drawer close remove all methods and then reinstate unavailable functionality
      externalLoadBoardHub.current.off(BROADCAST_LOAD_BOARD);
      externalLoadBoardHub.current.on(BROADCAST_LOAD_BOARD, (rawLoadData: any) => {
        if (rawLoadData.OrderStatus !== 'Available') {
          dispatch({ type: 'LOAD_BOARD_REMOVE_LOAD', value: rawLoadData })
        }
      });

    }

  }, [client, loadDrawerLoadId])//eslint-disable-line

  return (
    <LoadServiceHubsContext.Provider value={serviceConnection}>
      <CurrentLoadsContext.Provider value={setCurrentLoads}>
        <LoadDrawerIdContext.Provider value={setLoadDrawerId}>
          <SignalrRefetchContext.Provider value={[signalrRefetch, setSignalrRefetch]}>
            <UpdatedLoadsContext.Provider value={updatedLoads}>
              <UserCountContext.Provider value={userCount}>
                {children}
              </UserCountContext.Provider>
            </UpdatedLoadsContext.Provider>
          </SignalrRefetchContext.Provider>
        </LoadDrawerIdContext.Provider>
      </CurrentLoadsContext.Provider>
    </LoadServiceHubsContext.Provider>
  )
};

export const useConnectLoadDrawerToUserCount = (loadNumber: number) => {
  const currentUser = useContext(UserContext);
  const previousLoadNumber = useRef(null);
  const { loadServiceHub } = useContext(LoadServiceHubsContext);

  useEffect(() => {
    if (loadServiceHub && loadServiceHub?.connection?.state === CONNECTED) {
      if (loadNumber && previousLoadNumber.current === null) {
        loadServiceHub.invoke(JOIN_USER_CONNECTED_GROUP, { LoadNumber: loadNumber, UserId: currentUser.UserId });
      } else if (!loadNumber && previousLoadNumber.current !== null) {
        loadServiceHub.invoke(LEAVE_USER_CONNECTED_GROUP, true)
      } else if (loadNumber && previousLoadNumber.current !== loadNumber) {
        loadServiceHub.invoke(CHANGE_USER_CONNECTED_GROUP, { LoadNumber: loadNumber, UserId: currentUser.UserId });
      }

      previousLoadNumber.current = loadNumber
    }

  }, [currentUser.UserId, loadNumber, loadServiceHub]);

  useEffect(() => {
    return () => {
      if (loadServiceHub && loadServiceHub?.connection?.state === CONNECTED && previousLoadNumber.current) {
        loadServiceHub.invoke(LEAVE_USER_CONNECTED_GROUP, true)
      }
    }
  }, [loadServiceHub])
}

export const useLoadDetailSignalr = () => {
  const { loadNumber } = useParams<{ loadNumber: string }>();
  const currentUser = useContext(UserContext);
  const { loadServiceHub, LoadOverviewServiceHub } = useContext(LoadServiceHubsContext);
  const prevLoadNumber = useRef(null);
  const parsedLoadNumber = parseInt(loadNumber);

  useEffect(() => {
    if (loadServiceHub && LoadOverviewServiceHub && parsedLoadNumber && loadServiceHub?.connection?.state === CONNECTED && LoadOverviewServiceHub?.connection?.state === CONNECTED) {
      if (prevLoadNumber.current === null) {
        loadServiceHub.invoke(JOIN_USER_CONNECTED_GROUP, { LoadNumber: parsedLoadNumber, UserId: currentUser.UserId })
        LoadOverviewServiceHub.invoke(JOIN_LOAD_NUMBER_GROUP, { LoadNumber: parsedLoadNumber })
      } else if (prevLoadNumber.current !== parsedLoadNumber) {
        loadServiceHub.invoke(CHANGE_USER_CONNECTED_GROUP, { LoadNumber: parsedLoadNumber, UserId: currentUser.UserId });
        LoadOverviewServiceHub.invoke(CHANGE_LOAD_NUMBER_GROUP, { PreviousLoadNumber: prevLoadNumber.current, NextLoadNumber: parsedLoadNumber })
      }

      prevLoadNumber.current = parsedLoadNumber;
    } else if ((!loadServiceHub || !LoadOverviewServiceHub) || (loadServiceHub?.connection?.state !== CONNECTED || LoadOverviewServiceHub?.connection?.state !== CONNECTED)) {
      // If our connection status changed, reset load number so we can properly re-connect
      prevLoadNumber.current = null;
    }
  }, [LoadOverviewServiceHub, currentUser, loadServiceHub, parsedLoadNumber])
}

export const useSignalrWasLoadBooked = (loadNumber: number) => {
  const updatedLoads = useContext(UpdatedLoadsContext);
  const [wasLoadBooked, setLoadBooked] = useState(false);

  useEffect(() => {
    // if the BIN modal was opened from the LoadBoard updatedLoads may contain other loads that were also updated
    const loadBooked = updatedLoads.reduce((wasBooked: boolean, nextLoad: any) => {
      // depending on if the notification came from load overview or load board it will have different fields
      if (!wasBooked) {
        const loadStatus = nextLoad.OrderStatus || nextLoad.LoadStatus;
        return nextLoad.LoadNumber === loadNumber && loadStatus !== 'Available';
      }
      return wasBooked

    }, false)

    setLoadBooked(loadBooked);
  }, [loadNumber, updatedLoads])

  return wasLoadBooked;
};

export const useRemoveUpdatedLoadFromCache = (fieldName: string, __typename: string) => {
  //Evicts some cached item in response to a BROADCAST_LOAD_BOARD event
  const updatedLoads = useContext(UpdatedLoadsContext);
  const client = useApolloClient();

  useEffect(() => {
    if(updatedLoads?.length){
      const normalizedId = client.cache.identify({ LoadBoardId: updatedLoads[0][fieldName], __typename })
      client.cache.evict({ id: normalizedId });
      client.cache.gc();
    }
  }, [__typename, client.cache, fieldName, updatedLoads])
}
